/**
 * AdminVMDashboard
 *
 * Admin-only view of all provisioned VMs:
 * - List with status, IP, user association, uptime, monthly cost estimate
 * - Health check summary (last run, total/healthy/unhealthy)
 * - Quick actions: start, stop per VM
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import SeoHead from "../components/SeoHead";
import { getAuth } from 'firebase/auth';
import { devListFleetVms } from '../services/devFleetService';

// ============================================================================
// Types
// ============================================================================

interface VMRecord {
  uid: string;
  accountId: string;
  accountLabel: string | null;
  assignmentMode: string | null;
  environment: string | null;
  instanceName: string;
  zone: string;
  machineType: string;
  status: string;
  ipAddress: string | null;
  healthy: boolean | null;
  lastHealthCheck: number | null;
  createdAt: number | null;
  tenantId: string | null;
}

interface HealthSummary {
  lastRun: number | null;
  totalVMs: number;
  healthyCount: number;
  unhealthyCount: number;
  unhealthyVMs: string[];
}

// ============================================================================
// Helpers
// ============================================================================

function getProjectId(): string {
  return process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
}

function fnUrl(name: string): string {
  return `https://us-central1-${getProjectId()}.cloudfunctions.net/${name}`;
}

async function getAuthToken(): Promise<string | null> {
  const user = getAuth().currentUser;
  if (!user) return null;
  return user.getIdToken();
}

function formatDate(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// A stopped/suspended VM is NOT free (idle-lifecycle fix, 2026-08-18): the
// 50GB boot disk keeps billing (~$2/mo pd-standard, the live provisioners'
// default type), and a suspended VM additionally stores its RAM image
// (~$1/mo for 8GB). Only a DELETED instance with (at most) a snapshot
// (~$0.50/mo standard tier on used bytes) approaches zero. The old table
// counted TERMINATED as $0 and understated the fleet's real spend.
const STOPPED_DISK_COST_USD = 2.0;
const SUSPENDED_EXTRA_USD = 1.0;

function estimateMonthlyCostUsd(machineType: string, status?: string): number {
  const computeCosts: Record<string, number> = {
    'e2-standard-2': 49.0,
    'e2-standard-4': 98.0,
    'e2-standard-8': 196.0,
    'n1-standard-2': 68.0,
    'n1-standard-4': 136.0,
  };
  const s = (status ?? 'RUNNING').toUpperCase();
  if (s === 'DELETED' || s === 'DELETING') return 0.5; // snapshot-only remainder
  if (s === 'SUSPENDED') return STOPPED_DISK_COST_USD + SUSPENDED_EXTRA_USD;
  if (s === 'TERMINATED' || s === 'STOPPED' || s === 'STOPPING') return STOPPED_DISK_COST_USD;
  return (computeCosts[machineType] ?? 50) + STOPPED_DISK_COST_USD;
}

// ============================================================================
// Styled Components
// ============================================================================

const DashContainer = styled.div`
  padding: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh;
  background: var(--brand-canvas);

  @media (min-width: 1024px) { padding: 2.5rem; }
`;

const DashTitle = styled.h1`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.75rem;
  font-weight: 400;
  color: var(--brand-text);
  letter-spacing: -0.02em;
  text-transform: lowercase;
  margin-bottom: 1.5rem;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;

  @media (min-width: 600px) { grid-template-columns: repeat(4, 1fr); }
`;

const StatBox = styled.div<{ alert?: boolean }>`
  background: ${(p) => (p.alert ? '#FFF0ED' : 'white')};
  border: 1px solid ${(p) => (p.alert ? '#F5B7B1' : 'var(--brand-line)')};
  border-radius: 0.75rem;
  padding: 0.875rem 1rem;
  text-align: center;
`;

const StatValue = styled.div`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--brand-text);
  letter-spacing: -0.02em;
`;

const StatLabel = styled.div`
  font-size: 0.68rem;
  color: #888;
  margin-top: 0.2rem;
  text-transform: lowercase;
  letter-spacing: 0.05em;
`;

const TableCard = styled.div`
  background: white;
  border-radius: 1rem;
  border: 1px solid var(--brand-line);
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

const TableTitle = styled.div`
  padding: 1rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--brand-text);
  border-bottom: 1px solid var(--brand-line);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  overflow-x: auto;
  display: block;

  @media (min-width: 768px) { display: table; }
`;

const Th = styled.th`
  text-align: left;
  padding: 0.625rem 1rem;
  font-size: 0.68rem;
  text-transform: lowercase;
  letter-spacing: 0.05em;
  color: #AAA;
  font-weight: 600;
  background: var(--brand-canvas);
  border-bottom: 1px solid var(--brand-line);
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 0.75rem 1rem;
  color: #333;
  border-bottom: 1px solid var(--brand-surface);
  white-space: nowrap;
  vertical-align: middle;
`;

type StatusVariant = 'running' | 'stopped' | 'provisioning' | 'error' | 'unknown';

const STATUS_STYLES: Record<StatusVariant, { bg: string; color: string }> = {
  running:      { bg: '#F0FFF4', color: '#27AE60' },
  stopped:      { bg: '#F5F5F5', color: '#888' },
  provisioning: { bg: '#EDF7FF', color: '#2980B9' },
  error:        { bg: '#FFF0ED', color: '#C0392B' },
  unknown:      { bg: '#F5F5F5', color: '#AAA' },
};

function getStatusVariant(status: string): StatusVariant {
  const s = status.toUpperCase();
  if (s === 'RUNNING') return 'running';
  if (['STOPPED', 'TERMINATED'].includes(s)) return 'stopped';
  if (['PROVISIONING', 'STAGING', 'STOPPING', 'STARTING'].includes(s)) return 'provisioning';
  if (['DELETED', 'ERROR'].includes(s)) return 'error';
  return 'unknown';
}

const StatusPill = styled.span<{ variant: StatusVariant }>`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${(p) => STATUS_STYLES[p.variant as StatusVariant].bg};
  color: ${(p) => STATUS_STYLES[p.variant as StatusVariant].color};
`;

const ActionButton = styled.button<{ danger?: boolean }>`
  background: none;
  border: 1px solid ${(p) => (p.danger ? '#F5B7B1' : '#D4D4D0')};
  border-radius: 0.4rem;
  padding: 0.25rem 0.625rem;
  font-size: 0.75rem;
  color: ${(p) => (p.danger ? '#C0392B' : '#555')};
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s ease;
  margin-right: 0.35rem;

  &:hover:not(:disabled) {
    background: ${(p) => (p.danger ? '#FFF0ED' : 'var(--brand-surface)')};
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const RefreshButton = styled(ActionButton)`
  font-size: 0.8rem;
  padding: 0.3rem 0.75rem;
`;

const LoadingText = styled.div`
  color: #AAA;
  font-size: 0.875rem;
  padding: 2rem 1.25rem;
  text-align: center;
`;

const ErrorText = styled.div`
  color: #C0392B;
  font-size: 0.875rem;
  padding: 1rem;
`;

// ============================================================================
// Component
// ============================================================================

const AdminVMDashboard: React.FC = () => {
  const [vms, setVMs] = useState<VMRecord[]>([]);
  const [healthSummary, setHealthSummary] = useState<HealthSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionUid, setActionUid] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Developer toolkit (catalog v4): fleet data flows through the
      // developer-org-gated callable (developer.fleet.view enforced
      // server-side). The old direct collectionGroup read was cross-tenant
      // and rules-denied — this page rendered empty for everyone.
      const result = await devListFleetVms();
      setVMs(result.vms as VMRecord[]);
      setHealthSummary(result.healthSummary);
    } catch (e: unknown) {
      const code = (e as { code?: string } | null)?.code ?? '';
      setError(
        code === 'functions/permission-denied'
          ? 'Access denied: fleet view requires developer access.'
          : 'Failed to load VM data'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStop = useCallback(async (uid: string) => {
    setActionUid(uid);
    try {
      const token = await getAuthToken();
      if (!token) return;
      // Admin calls the function on behalf of the user — requires a uid override
      // For now, admins manage their own VM (extend with admin endpoint as needed)
      await fetch(fnUrl('stopVM'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUid: uid }),
      });
      await fetchData();
    } catch {
      // Non-fatal for admin
    } finally {
      setActionUid(null);
    }
  }, [fetchData]);

  const handleStart = useCallback(async (uid: string) => {
    setActionUid(uid);
    try {
      const token = await getAuthToken();
      if (!token) return;
      await fetch(fnUrl('startVM'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUid: uid }),
      });
      await fetchData();
    } catch {
      // Non-fatal for admin
    } finally {
      setActionUid(null);
    }
  }, [fetchData]);

  const totalMonthlyCost = vms.reduce(
    (sum, vm) => sum + estimateMonthlyCostUsd(vm.machineType, vm.status),
    0
  );

  const runningCount = vms.filter((v) => v.status === 'RUNNING').length;
  const unhealthyCount = vms.filter((v) => v.healthy === false).length;

  return (
    <DashContainer>
      <SeoHead
        title="Admin Dashboard | prest0"
        description="Prest0 admin dashboard for managing virtual machines and platform settings."
        noIndex={true}
      />
      <DashTitle>vm dashboard</DashTitle>

      {/* Summary stats */}
      <SummaryGrid>
        <StatBox>
          <StatValue>{vms.length}</StatValue>
          <StatLabel>Total VMs</StatLabel>
        </StatBox>
        <StatBox>
          <StatValue>{runningCount}</StatValue>
          <StatLabel>Running</StatLabel>
        </StatBox>
        <StatBox alert={unhealthyCount > 0}>
          <StatValue style={{ color: unhealthyCount > 0 ? '#C0392B' : 'var(--brand-text)' }}>
            {unhealthyCount}
          </StatValue>
          <StatLabel>Unhealthy</StatLabel>
        </StatBox>
        <StatBox>
          <StatValue>${totalMonthlyCost.toFixed(0)}</StatValue>
          <StatLabel>Est. / Month</StatLabel>
        </StatBox>
      </SummaryGrid>

      {/* Health check summary */}
      {healthSummary && (
        <div style={{ marginBottom: '1rem', fontSize: '0.8rem', color: '#888' }}>
          Last health check: {formatDate(healthSummary.lastRun)} —{' '}
          {healthSummary.healthyCount}/{healthSummary.totalVMs} healthy
          {healthSummary.unhealthyVMs.length > 0 && (
            <span style={{ color: '#C0392B' }}>
              {' '}— unhealthy: {healthSummary.unhealthyVMs.join(', ')}
            </span>
          )}
        </div>
      )}

      {/* VM table */}
      <TableCard>
        <TableTitle>
          All Provisioned VMs
          <RefreshButton onClick={fetchData} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </RefreshButton>
        </TableTitle>

        {loading ? (
          <LoadingText>Loading VM data…</LoadingText>
        ) : error ? (
          <ErrorText>{error}</ErrorText>
        ) : vms.length === 0 ? (
          <LoadingText>No VMs provisioned yet.</LoadingText>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Instance</Th>
                <Th>Account Mapping</Th>
                <Th>Status</Th>
                <Th>IP Address</Th>
                <Th>Machine Type</Th>
                <Th>Est. Cost/Mo</Th>
                <Th>Last Check</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {vms.map((vm) => (
                <tr key={vm.uid}>
                  <Td>
                    <code style={{ fontSize: '0.8rem', background: 'var(--brand-surface)', padding: '0.1rem 0.4rem', borderRadius: '0.3rem' }}>
                      {vm.instanceName || '—'}
                    </code>
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                      <span style={{ fontSize: '0.75rem', color: '#888', fontFamily: 'monospace' }}>
                        uid: {vm.uid.slice(0, 12)}…
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#444', fontFamily: 'monospace' }}>
                        acct: {vm.accountId.slice(0, 12)}…
                      </span>
                      {vm.accountLabel && (
                        <span style={{ fontSize: '0.7rem', background: '#EEF6FF', color: '#2C5AA0', padding: '0.1rem 0.35rem', borderRadius: '0.3rem', width: 'fit-content' }}>
                          {vm.accountLabel}
                        </span>
                      )}
                      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                        {vm.assignmentMode && (
                          <span style={{ fontSize: '0.68rem', background: 'var(--brand-surface)', color: '#666', padding: '0.08rem 0.35rem', borderRadius: '0.3rem' }}>
                            {vm.assignmentMode}
                          </span>
                        )}
                        {vm.environment && (
                          <span style={{ fontSize: '0.68rem', background: '#F0FFF4', color: '#1E8449', padding: '0.08rem 0.35rem', borderRadius: '0.3rem' }}>
                            {vm.environment}
                          </span>
                        )}
                        {vm.tenantId && (
                          <span style={{ fontSize: '0.68rem', background: '#F0F0FF', color: '#5555AA', padding: '0.08rem 0.35rem', borderRadius: '0.3rem' }}>
                            {vm.tenantId}
                          </span>
                        )}
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <StatusPill variant={getStatusVariant(vm.status)}>
                      {vm.status}
                    </StatusPill>
                  </Td>
                  <Td>
                    {vm.ipAddress ? (
                      <code style={{ fontSize: '0.8rem' }}>{vm.ipAddress}</code>
                    ) : (
                      <span style={{ color: '#CCC' }}>—</span>
                    )}
                  </Td>
                  <Td>{vm.machineType || '—'}</Td>
                  <Td>${estimateMonthlyCostUsd(vm.machineType, vm.status).toFixed(0)}</Td>
                  <Td style={{ color: '#AAA', fontSize: '0.78rem' }}>
                    {formatDate(vm.lastHealthCheck)}
                  </Td>
                  <Td>
                    {vm.status === 'RUNNING' ? (
                      <ActionButton
                        danger
                        onClick={() => handleStop(vm.uid)}
                        disabled={actionUid === vm.uid}
                      >
                        Stop
                      </ActionButton>
                    ) : vm.status === 'TERMINATED' || vm.status === 'STOPPED' ? (
                      <ActionButton
                        onClick={() => handleStart(vm.uid)}
                        disabled={actionUid === vm.uid}
                      >
                        Start
                      </ActionButton>
                    ) : (
                      <span style={{ color: '#CCC', fontSize: '0.75rem' }}>—</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableCard>
    </DashContainer>
  );
};

export default AdminVMDashboard;
